import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/useAuth";

/**
 * The top-right of a public page: "Log in / Sign up" to a visitor, "Dashboard" to somebody already
 * signed in.
 *
 * <p>One component for all three public headers — the landing page, {@link MarketplaceShell} and
 * {@link PublicPageShell} — because the rule is the same on each and three copies of it would
 * eventually disagree about who is signed in.</p>
 *
 * <p><b>It renders nothing at all while the auth check is in flight.</b> `isAuthenticated` is false
 * during that window as well as when genuinely signed out, so rendering eagerly would show every
 * returning customer "Sign up" for a beat and then swap it — an invitation to create a second
 * account, on the page most likely to be someone's first click. An empty gap for a moment is the
 * lesser fault, and the header reserves its height either way.</p>
 */
export default function PublicAuthActions({
  linkStyle,
  pillStyle,
  pillClassName,
  gap = 22,
  onNavigate,
  className,
  style,
}: {
  /** The plain text action ("Log in"). Also used for "Dashboard" when signed in. */
  linkStyle: CSSProperties;
  /** The filled action ("Sign up"). */
  pillStyle: CSSProperties;
  pillClassName?: string;
  gap?: number;
  /** For the burger menu, which has to close itself on the way out. */
  onNavigate?: () => void;
  /**
   * Applied to the row itself, so callers don't wrap this in a second flex container. The landing
   * page needs both: `.pn-auth` (hidden at phone widths in favour of the burger menu) and, in that
   * menu, the margin that separates the pair from the nav links above it. A wrapping div would
   * swallow the `flex: 1` the menu's buttons use to share the width.
   */
  className?: string;
  style?: CSSProperties;
}) {
  const { isAuthenticated, isLoading } = useAuth();

  const wrap = (children: ReactNode) => (
    <div className={className} style={{ display: "flex", alignItems: "center", gap, ...style }}>
      {children}
    </div>
  );

  // Not yet known. See the note above: guessing "signed out" here is the costly guess.
  if (isLoading) return wrap(null);

  if (isAuthenticated) {
    return wrap(
      <Link
        to="/dashboard"
        data-testid="public-dashboard-link"
        className={pillClassName}
        style={pillStyle}
        onClick={onNavigate}
      >
        Dashboard
      </Link>,
    );
  }

  return wrap(
    <>
      <Link to="/login" data-testid="public-login-link" style={linkStyle} onClick={onNavigate}>
        Log in
      </Link>
      <Link
        to="/signup"
        data-testid="public-signup-link"
        className={pillClassName}
        style={pillStyle}
        onClick={onNavigate}
      >
        Sign up
      </Link>
    </>,
  );
}
